import React from "react";
import AnonymousPage from "@/models/base/AnonymousPage";
import {PageContainer, PageHeader} from "@reapptor-apps/reapptor-react-components";
import {DynamicPageResponseSingleType} from "@/models/cms/DynamicPageResponse";
import styles from "./Newsletters.module.scss";
import {BasePageParameters, ch} from "@reapptor-apps/reapptor-react-common";
import Localizer from "@/localization/Localizer";
import MetaTags from "@/components/MetaTags/MetaTags";

const sections = ["general", "occupational", "subscription"] as const;
const defaultSection = "subscription";
type PageSection = typeof sections[number];

export interface INewslettersParams extends BasePageParameters {
  section?: PageSection;
}

interface INewslettersProps {}

interface INewslettersState {
  selectedSection: string;
  selectedIframeUrl: string | null;
  general: string | null;
  occupational: string | null;
  subscription: string | null;
}


export default class Newsletters extends AnonymousPage<INewslettersProps, INewslettersState> {
  state: INewslettersState = {
    selectedSection: defaultSection,
    selectedIframeUrl: "loading",
    general: null,
    occupational: null,
    subscription: null,
  };

  // Defined inside component to support language switching after page load
  private pageSections: Array<{ id: PageSection; sectionIcon: string; label: string; }> = [
    /* Hidden until Fenix has newsletters ready to embed 
    { 
      id: "general", 
      sectionIcon: "https://www.laakarikeskusfenix.fi/strapi-images/stethoscope_fd7f7e42fa.svg", 
      label: Localizer.newslettersPageGeneralSectionTitle 
    },
    { 
      id: "occupational", 
      sectionIcon: "https://www.laakarikeskusfenix.fi/strapi-images/first_aid_kit_51ba677ebf.svg", 
      label: Localizer.newslettersPageOccupationalSectionTitle 
    },*/
    { 
      id: "subscription", 
      sectionIcon: "https://www.laakarikeskusfenix.fi/strapi-images/thumbnail_etusivuikoni_chat_0d99cde7d7.png", 
      label: Localizer.newslettersPageSubscriptionSectionTitle 
    }
  ];

  async initializeAsync(): Promise<void> {
    await super.initializeAsync();
    const response: DynamicPageResponseSingleType = await this.getAsync("/api/Application/GetDynamicPageContent?contentType=newsletters-page&populate=*");

    const { generalNewsletterURL = null,
      occupationalHealthcareNewsletterURL = null,
      subscriptionFormURL = null,
    } = response?.data?.attributes ?? {};
    
    const urls = {
      general: generalNewsletterURL,
      occupational: occupationalHealthcareNewsletterURL,
      subscription: subscriptionFormURL,
    };
    await this.setState({ ...urls });
    
    const params = this.parameters as INewslettersParams | null;
    const selectedSection = params?.section && sections.includes(String(params.section) as PageSection) ? params.section as PageSection : defaultSection;
    const selectedIframeUrl = this.state[selectedSection] ?? null;
    await this.setState({ selectedSection, selectedIframeUrl });
  }

  public getTitle(): string {
    return Localizer.newslettersPageTitle;
  }

  public async onPopState(e: PopStateEvent): Promise<void> {
    await this.selectSection(e.state.section);
    window.scrollTo(0, 0);
  }

  public async selectSection(sectionId: string | null | undefined): Promise<void> {
    const selectedSection = sectionId && sections.includes(String(sectionId) as PageSection) ? sectionId as PageSection : defaultSection;
    const selectedIframeUrl = this.state[selectedSection] ?? null;
    await this.setState({ selectedSection, selectedIframeUrl });
  }

  componentDidMount(): Promise<void> {
    window.addEventListener('popstate', e => this.onPopState(e));
    return super.componentDidMount();
  }

  componentWillUnmount(): Promise<void> {
    window.removeEventListener("popstate", e => this.onPopState(e));
    return super.componentWillUnmount();
  }

  public render(): React.ReactNode {
    return (
        <PageContainer fullWidth className={styles.pageContainer}>
          <MetaTags pageName="Newsletters" />
          <PageHeader title={Localizer.newslettersPageTitle} className={styles.pageHeader} />

          <div className={styles.pageContent}>
            <div className={styles.fluidContent}>
              <div className="row m-0 p-0">
                <div id={"left_menu"} className={"col-md-4"} style={{paddingTop: "3rem"}}>
                  {this.pageSections.map(({ id, label, sectionIcon }) => (
                    <div key={id}>
                      <div
                          className={
                            this.state.selectedSection == id
                                ? this.css(styles.selected, styles.listItem)
                                : styles.listItem
                          }
                          onClick={async () => {
                            await this.selectSection(id);
                            window.history.pushState({ section: id }, "", `?section=${id}`);
                            if (ch.mobile) {
                              let objDiv = document.getElementById("left_menu");
                              window.scrollTo(0, objDiv?.scrollHeight ?? document.body.scrollHeight);
                            } else {
                              window.scrollTo(0, 0);
                            }
                          }}
                      >
                        <img src={sectionIcon} height={40} width={40} alt="alt" />
                        <div>{label}</div>
                      </div>
                    </div>))}
                </div>
                <div className={"col-md-8"} style={{paddingTop: "3rem"}}>
                  {this.state.selectedSection ? (
                    <div className={styles.newsletterContainer}>
                      {this.state.selectedIframeUrl ? (
                          this.state.selectedIframeUrl === "loading" ? "Ladataan..." : (
                              <iframe src={this.state.selectedIframeUrl} title={this.state.selectedSection}></iframe>
                          )
                      ) : (<span>Uutiskirjeen URL-osoitetta ei ole saatavilla</span>)}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </PageContainer>
    );
  }
}
